import { z } from 'zod';
const providerRateWindowSchema = z.object({
    sizeSeconds: z.number().positive(),
    maxRequests: z.number().int().nonnegative()
});
const providerRatePolicyOverrideSchema = z.object({
    maxConcurrent: z.number().int().positive().optional(),
    windows: z.array(providerRateWindowSchema).optional(),
    cooldownSeconds: z.number().int().nonnegative().optional(),
    description: z.string().optional()
});
export const BASE_PROVIDER_RATE_POLICIES = {
    default: {
        key: 'default',
        description: 'Fallback policy for unspecified providers/models.',
        maxConcurrent: 10,
        windows: [{ sizeSeconds: 60, maxRequests: 600 }]
    },
    'replicate:default': {
        key: 'replicate:default',
        description: 'General Replicate usage baseline (limit 600 RPM).',
        maxConcurrent: 12,
        windows: [{ sizeSeconds: 60, maxRequests: 500 }]
    },
    'fal:default': {
        key: 'fal:default',
        description: 'FAL platform queue baseline (10 concurrent, 600 RPM).',
        maxConcurrent: 10,
        windows: [{ sizeSeconds: 60, maxRequests: 600 }]
    },
    'fal:sora': {
        key: 'fal:sora',
        description: 'Sora endpoints routed through FAL.',
        maxConcurrent: 10,
        windows: [{ sizeSeconds: 60, maxRequests: 120 }]
    },
    'elevenlabs:transcription': {
        key: 'elevenlabs:transcription',
        description: 'ElevenLabs Scribe speech-to-text (Pro tier).',
        maxConcurrent: 40,
        windows: [{ sizeSeconds: 60, maxRequests: 80 }]
    },
    'google:lyria-3-pro': {
        key: 'google:lyria-3-pro',
        description: 'Google Lyria 3 Pro music generation.',
        maxConcurrent: 4,
        windows: [{ sizeSeconds: 60, maxRequests: 30 }]
    },
    'openai:default': {
        key: 'openai:default',
        description: 'OpenAI generic chat/text fallback.',
        maxConcurrent: 6,
        windows: [{ sizeSeconds: 60, maxRequests: 500 }]
    },
    'openai:gpt-4o': {
        key: 'openai:gpt-4o',
        description: 'OpenAI GPT-4o family shared limits (subset of 10k RPM).',
        maxConcurrent: 8,
        windows: [{ sizeSeconds: 60, maxRequests: 600 }]
    },
    'openai:sora-2': {
        key: 'openai:sora-2',
        description: 'OpenAI Sora-2 video generations (25 RPM).',
        maxConcurrent: 1,
        windows: [{ sizeSeconds: 60, maxRequests: 25 }]
    },
    'openai:sora-2-pro': {
        key: 'openai:sora-2-pro',
        description: 'OpenAI Sora-2 Pro video generations (10 RPM).',
        maxConcurrent: 1,
        windows: [{ sizeSeconds: 60, maxRequests: 10 }]
    }
};
const mergePolicies = (base, override) => {
    if (!override) {
        return base;
    }
    return {
        key: base.key,
        description: override.description ?? base.description,
        maxConcurrent: override.maxConcurrent ?? base.maxConcurrent,
        windows: override.windows ?? base.windows,
        cooldownSeconds: override.cooldownSeconds ?? base.cooldownSeconds
    };
};
const overridesSchema = z.record(providerRatePolicyOverrideSchema);
export const parseProviderRateOverrides = (value) => {
    if (!value) {
        return undefined;
    }
    try {
        const raw = JSON.parse(value);
        const parsed = overridesSchema.parse(raw);
        return parsed;
    }
    catch (error) {
        throw new Error(`Failed to parse PROVIDER_RATE_LIMITS overrides: ${error instanceof Error ? error.message : String(error)}`);
    }
};
export const resolveProviderRatePolicy = (target, overrides) => {
    const candidates = [];
    if (target.key) {
        candidates.push(target.key);
    }
    if (target.model) {
        candidates.push(`${target.provider}:${target.model}`);
    }
    if (target.variant) {
        candidates.push(`${target.provider}:${target.variant}`);
    }
    candidates.push(`${target.provider}:default`, 'default');
    const mergedOverrides = overrides ?? {};
    for (const candidate of candidates) {
        const base = BASE_PROVIDER_RATE_POLICIES[candidate];
        if (!base) {
            continue;
        }
        return mergePolicies(base, mergedOverrides[candidate]);
    }
    // If no base match, synthesize from overrides or fall back to global default.
    for (const candidate of candidates) {
        const override = mergedOverrides[candidate];
        if (override) {
            const fallback = BASE_PROVIDER_RATE_POLICIES.default;
            return mergePolicies({ ...fallback, key: candidate }, override);
        }
    }
    return BASE_PROVIDER_RATE_POLICIES.default;
};
//# sourceMappingURL=provider-rate.js.map